import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { MESSAGE_STATUS } from "./assistantTypes";
import {
  deleteConversation,
  fetchConversation,
  fetchConversations,
} from "./conversationClient";

const SERVICE_LABELS = { scapp: "Scapp", nanoox: "Nanoox", flux: "Flux" };
const ALL_SERVICES = Object.keys(SERVICE_LABELS);

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("es-CL", {
      day: "numeric",
      month: "short",
    });
  } catch {
    return "";
  }
}

function formatLastContext(ctx) {
  if (!ctx) return null;
  const parts = [];
  const ci = ctx.center_info || {};
  if (ci.centro) parts.push(ci.centro);
  const dr = ctx.date_range || {};
  if (dr.startDate && dr.endDate) {
    parts.push(`${dr.startDate} – ${dr.endDate}`);
  } else if (dr.dayRange) {
    parts.push(`${dr.dayRange} días`);
  }
  return parts.length ? parts.join(" · ") : null;
}

function buildRestoredMessages(rawMessages) {
  return rawMessages.map((m) => ({
    id:
      typeof crypto !== "undefined" && crypto.randomUUID
        ? crypto.randomUUID()
        : `msg-${Date.now()}-${Math.random().toString(16).slice(2)}`,
    role: m.role,
    content: m.content,
    status: MESSAGE_STATUS.DONE,
    createdAt: m.created_at,
    metadata: {},
    sources: Array.isArray(m.sources) ? m.sources : [],
  }));
}

export default function ConversationHistory({
  agentBaseUrl,
  currentService,
  onRestore,
  onClose,
}) {
  const username = localStorage.getItem("username") || "";
  const [activeService, setActiveService] = useState(currentService || null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!agentBaseUrl || !username) return;
    setLoading(true);
    setError("");
    try {
      const data = await fetchConversations({
        agentBaseUrl,
        username,
        service: activeService || undefined,
      });
      setConversations(data);
    } catch (err) {
      setError(err.message || "Error al cargar el historial");
    } finally {
      setLoading(false);
    }
  }, [agentBaseUrl, username, activeService]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSelect = useCallback(
    async (conv) => {
      if (loadingId) return;
      setLoadingId(conv.id);
      try {
        const data = await fetchConversation({
          agentBaseUrl,
          conversationId: conv.id,
          username,
        });
        const restored = buildRestoredMessages(data.messages || []);
        onRestore(conv.id, restored);
        onClose();
      } catch (err) {
        setError(err.message || "Error al cargar la conversación");
      } finally {
        setLoadingId(null);
      }
    },
    [agentBaseUrl, username, loadingId, onRestore, onClose]
  );

  const handleDelete = useCallback(
    async (e, conv) => {
      e.stopPropagation();
      if (deletingId) return;
      setDeletingId(conv.id);
      try {
        await deleteConversation({ agentBaseUrl, conversationId: conv.id, username });
        setConversations((prev) => prev.filter((c) => c.id !== conv.id));
      } catch (err) {
        setError(err.message || "Error al eliminar");
      } finally {
        setDeletingId(null);
      }
    },
    [agentBaseUrl, username, deletingId]
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {/* Filter chips */}
      <Box sx={{ px: 2, py: 1.2, display: "flex", gap: 0.8, flexWrap: "wrap" }}>
        <Chip
          label="Todos"
          size="small"
          onClick={() => setActiveService(null)}
          variant={activeService === null ? "filled" : "outlined"}
          sx={chipSx(activeService === null)}
        />
        {ALL_SERVICES.map((svc) => (
          <Chip
            key={svc}
            label={SERVICE_LABELS[svc]}
            size="small"
            onClick={() => setActiveService(activeService === svc ? null : svc)}
            variant={activeService === svc ? "filled" : "outlined"}
            sx={chipSx(activeService === svc)}
          />
        ))}
      </Box>

      {/* List */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: 1.5,
          pb: 1.5,
          display: "flex",
          flexDirection: "column",
          gap: 0.6,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(255,255,255,0.10)",
            borderRadius: 999,
          },
        }}
      >
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
            <CircularProgress size={22} sx={{ color: "#ff9154" }} />
          </Box>
        )}

        {!loading && error && (
          <Typography sx={{ color: "rgba(255,120,80,0.85)", fontSize: "0.8rem", mt: 2, textAlign: "center" }}>
            {error}
          </Typography>
        )}

        {!loading && !error && conversations.length === 0 && (
          <Typography sx={{ color: "rgba(255,255,255,0.3)", fontSize: "0.82rem", mt: 3, textAlign: "center" }}>
            Sin conversaciones guardadas
          </Typography>
        )}

        {!loading &&
          conversations.map((conv) => {
            const ctxLabel = formatLastContext(conv.last_context);
            const isLoading = loadingId === conv.id;
            const isDeleting = deletingId === conv.id;
            return (
              <Box
                key={conv.id}
                onClick={() => handleSelect(conv)}
                sx={{
                  px: 1.4,
                  py: 1,
                  borderRadius: 2,
                  cursor: isLoading ? "wait" : "pointer",
                  border: "1px solid rgba(255,255,255,0.06)",
                  bgcolor: "rgba(255,255,255,0.03)",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  transition: "background-color 120ms",
                  "&:hover": { bgcolor: "rgba(255,255,255,0.07)" },
                  opacity: isLoading || isDeleting ? 0.55 : 1,
                }}
              >
                {/* Leading dot */}
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: serviceColor(conv.service),
                    mt: "6px",
                    flexShrink: 0,
                  }}
                />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: "0.82rem",
                      fontWeight: 500,
                      color: "#d8eaf8",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {conv.title || "Sin título"}
                  </Typography>
                  <Stack direction="row" spacing={0.6} sx={{ mt: 0.3, flexWrap: "wrap" }}>
                    <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.38)" }}>
                      {SERVICE_LABELS[conv.service] || conv.service}
                    </Typography>
                    {ctxLabel && (
                      <>
                        <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.2)" }}>·</Typography>
                        <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.38)" }}>
                          {ctxLabel}
                        </Typography>
                      </>
                    )}
                    <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.2)" }}>·</Typography>
                    <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.3)" }}>
                      {formatDate(conv.updated_at)}
                    </Typography>
                    {conv.message_count > 0 && (
                      <>
                        <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.2)" }}>·</Typography>
                        <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.3)" }}>
                          {conv.message_count} msg
                        </Typography>
                      </>
                    )}
                  </Stack>
                </Box>

                <Tooltip title="Eliminar">
                  <span>
                    <IconButton
                      size="small"
                      onClick={(e) => handleDelete(e, conv)}
                      disabled={isDeleting || isLoading}
                      sx={{
                        color: "rgba(255,255,255,0.28)",
                        p: 0.4,
                        flexShrink: 0,
                        "&:hover": { color: "rgba(255,90,60,0.8)" },
                      }}
                    >
                      {isDeleting ? (
                        <CircularProgress size={13} sx={{ color: "inherit" }} />
                      ) : (
                        <DeleteOutlineRoundedIcon sx={{ fontSize: 15 }} />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            );
          })}
      </Box>
    </Box>
  );
}

function chipSx(active) {
  return {
    fontSize: "0.72rem",
    height: 24,
    bgcolor: active ? "rgba(232,78,14,0.22)" : "rgba(255,255,255,0.05)",
    color: active ? "#ff9154" : "rgba(255,255,255,0.45)",
    border: active ? "1px solid rgba(232,78,14,0.4)" : "1px solid rgba(255,255,255,0.1)",
    "&:hover": { bgcolor: active ? "rgba(232,78,14,0.3)" : "rgba(255,255,255,0.09)" },
    "& .MuiChip-label": { px: 1 },
  };
}

function serviceColor(service) {
  if (service === "scapp") return "#7ed4ff";
  if (service === "nanoox") return "#ff9154";
  if (service === "flux") return "#8bd48b";
  return "rgba(255,255,255,0.25)";
}
